import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import ApiService from "../../services/api";
import PatientFooter from "./PatientFooter";
import PatientHeader from "./PatientHeader";

import "./checkout.css";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";

interface Product {
  product_id: number;
  name: string;
  description: string;
  price: number;
  final_price: number;
  features: string[];
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface PaymentResponse {
  data: {
    order_id: string;
    payment_status: string;
    payment_url: string;
  };
  message: string;
  success: boolean;
}

const PatientCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { primaryColor, isWhiteLabel, contactLink } = useWhiteLabel();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<
    "pending" | "success" | "failed"
  >("pending");

  // Form states
  const [cardInfo, setCardInfo] = useState({
    cardNumber: "",
    cardholderName: "",
    expiryDate: "",
    cvv: "",
  });

  const [billingAddress, setBillingAddress] = useState({
    address: "",
    city: "",
    state: "",
    country: "US",
    postalCode: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Cart items from localStorage
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      setCartItems(parsedCart);
    }
  }, []);

  const handleOrderReceipt = async () => {
    try {
      const response = await ApiService.getOrderReceipt(orderId);

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `order_receipt_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading receipt:", error);
    }
  };

  // Save cart to localStorage whenever it changes
  const saveCartToLocalStorage = (items: CartItem[]) => {
    localStorage.setItem("cart", JSON.stringify(items));
  };

  // Calculate subtotal dynamically
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.final_price * item.quantity,
    0
  );

  // Delete item function
  const removeItem = (productId: number) => {
    const updatedCart = cartItems.filter(
      (item) => item.product.product_id !== productId
    );
    setCartItems(updatedCart);
    saveCartToLocalStorage(updatedCart);
  };

  // Update quantity function
  const updateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(productId);
      return;
    }
    const updatedCart = cartItems.map((item) =>
      item.product.product_id === productId
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCartItems(updatedCart);
    saveCartToLocalStorage(updatedCart);
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "cardNumber":
        const cleanNumber = value.replace(/\s/g, "");
        if (!cleanNumber) error = "Card number is required";
        else if (cleanNumber.length < 13 || cleanNumber.length > 19)
          error = "Invalid card number";
        break;
      case "cardholderName":
        if (!value.trim()) error = "Cardholder name is required";
        break;
      case "expiryDate":
        if (!value) error = "Expiry date is required";
        else if (!/^\d{2}\/\d{2}$/.test(value))
          error = "Invalid format (MM/YY)";
        else {
          const [month, year] = value.split("/");
          const monthNum = parseInt(month);
          if (monthNum < 1 || monthNum > 12) error = "Invalid month";
        }
        break;
      case "cvv":
        if (!value) error = "CVV is required";
        else if (!/^\d{3,4}$/.test(value)) error = "Invalid CVV";
        break;
      case "address":
        if (!value.trim()) error = "Address is required";
        break;
      case "city":
        if (!value.trim()) error = "City is required";
        break;
      case "state":
        if (!value.trim()) error = "State is required";
        break;
      case "postalCode":
        if (!value.trim()) error = "Postal code is required";
        break;
    }

    return error;
  };

  const handleCardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Format card number with spaces
    if (name === "cardNumber") {
      const formatted =
        value
          .replace(/\s/g, "")
          .match(/.{1,4}/g)
          ?.join(" ") || value;
      setCardInfo((prev) => ({ ...prev, [name]: formatted }));
    } else if (name === "expiryDate") {
      // Format MM/YY
      const formatted = value.replace(/\D/g, "").match(/(\d{0,2})(\d{0,2})/)!;
      const result = formatted[1] + (formatted[2] ? "/" + formatted[2] : "");
      setCardInfo((prev) => ({ ...prev, [name]: result }));
    } else {
      setCardInfo((prev) => ({ ...prev, [name]: value }));
    }

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(
        name,
        name === "cardNumber" || name === "expiryDate"
          ? name === "cardNumber"
            ? value.replace(/\s/g, "")
            : value
          : value
      );
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const value =
      name === "cardNumber"
        ? cardInfo.cardNumber
        : name === "cardholderName"
        ? cardInfo.cardholderName
        : name === "expiryDate"
        ? cardInfo.expiryDate
        : name === "cvv"
        ? cardInfo.cvv
        : name === "address"
        ? billingAddress.address
        : name === "city"
        ? billingAddress.city
        : name === "state"
        ? billingAddress.state
        : name === "postalCode"
        ? billingAddress.postalCode
        : "";
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBillingInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({ ...prev, [name]: value }));

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate card info
    const cardNumberError = validateField(
      "cardNumber",
      cardInfo.cardNumber.replace(/\s/g, "")
    );
    const cardholderNameError = validateField(
      "cardholderName",
      cardInfo.cardholderName
    );
    const expiryDateError = validateField("expiryDate", cardInfo.expiryDate);
    const cvvError = validateField("cvv", cardInfo.cvv);

    // Validate billing address
    const addressError = validateField("address", billingAddress.address);
    const cityError = validateField("city", billingAddress.city);
    const stateError = validateField("state", billingAddress.state);
    const postalCodeError = validateField(
      "postalCode",
      billingAddress.postalCode
    );

    if (cardNumberError) newErrors.cardNumber = cardNumberError;
    if (cardholderNameError) newErrors.cardholderName = cardholderNameError;
    if (expiryDateError) newErrors.expiryDate = expiryDateError;
    if (cvvError) newErrors.cvv = cvvError;
    if (addressError) newErrors.address = addressError;
    if (cityError) newErrors.city = cityError;
    if (stateError) newErrors.state = stateError;
    if (postalCodeError) newErrors.postalCode = postalCodeError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add items before proceeding.");
      return;
    }

    // Mark all fields as touched
    setTouched({
      cardNumber: true,
      cardholderName: true,
      expiryDate: true,
      cvv: true,
      address: true,
      city: true,
      state: true,
      postalCode: true,
    });

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Get expiry month and year
      const [month, year] = cardInfo.expiryDate.split("/");
      const fullYear = `20${year}`;

      // Prepare request payload
      const payload = {
        clinic_id: 1, // You might want to get this from user context
        products: cartItems.map((item) => ({
          product_id: item.product.product_id,
          patient_product_price_id: item.product.product_id, // Using product_id as price_id for now
        })),
        payment_details: {
          address: billingAddress.address,
          city: billingAddress.city,
          state: billingAddress.state,
          postalcode: billingAddress.postalCode,
          country: billingAddress.country,
          cardname: cardInfo.cardholderName,
          cardnumber: cardInfo.cardNumber.replace(/\s/g, ""),
          month: month,
          year: fullYear,
          cvv: cardInfo.cvv,
        },
      };

      const data: PaymentResponse = await ApiService.createOrder(payload);

      if (data.success && data.data.payment_url) {
        setPaymentUrl(data.data.payment_url);
        setOrderId(data.data.order_id);
        setCurrentStep(2);
        // Clear cart after successful order creation
        localStorage.removeItem("cart");
      } else {
        throw new Error(data.message || "Payment failed");
      }
    } catch (err: any) {
      setErrors({
        submit:
          err?.response?.data?.message ||
          err?.message ||
          "Payment processing failed. Please try again.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Step icons component
  const StepIcon = ({
    step,
    isActive,
    isCompleted,
  }: {
    step: number;
    isActive: boolean;
    isCompleted: boolean;
  }) => {
    const icons = {
      1: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
          />
        </svg>
      ),
      2: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
      3: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };

    const getBackgroundColor = () => {
      if (isCompleted) return "bg-green-600 text-white";
      if (isActive) {
        return isWhiteLabel ? "text-white" : "bg-blue-600 text-white";
      }
      return "bg-gray-200 text-gray-500";
    };

    const activeStyle =
      isActive && isWhiteLabel ? { backgroundColor: primaryColor } : {};

    return (
      <div
        className={`
        w-12 h-12 rounded-full flex items-center justify-center ${getBackgroundColor()}`}
        style={activeStyle}
      >
        {isCompleted ? (
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          icons[step as keyof typeof icons]
        )}
      </div>
    );
  };

  // Step 1: Cart Review with Delete Functionality
  if (currentStep === 1) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Header */}
        <PatientHeader />

        {/* Progress Steps */}
        <div className="bg-white py-8">
          <div className="checkoutTab mx-auto overflow-x-auto px-4 sm:px-6 lg:px-8">
            <div className="innerCheckoutTab flex items-center justify-center  ">
              <div className="flex items-center">
                <StepIcon
                  step={1}
                  isActive={currentStep >= 1}
                  isCompleted={currentStep > 1}
                />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Your cart</p>
                  <p className="text-xs text-gray-500">Review items</p>
                </div>
              </div>

              <div className="w-24 mx-4">
                <div className="h-0.5 bg-gray-300 relative">
                  <div
                    className={`absolute left-0 top-0 h-full transition-all ${
                      currentStep > 1 ? "w-full" : "w-0"
                    }`}
                    style={{ backgroundColor: primaryColor }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center">
                <StepIcon
                  step={2}
                  isActive={currentStep >= 2}
                  isCompleted={currentStep > 2}
                />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Payment</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>

              <div className="w-24 mx-4">
                <div className="h-0.5 bg-gray-300 relative">
                  <div
                    className={`absolute left-0 top-0 h-full transition-all ${
                      currentStep > 2 ? "w-full" : "w-0"
                    }`}
                    style={{ backgroundColor: primaryColor }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center">
                <StepIcon
                  step={3}
                  isActive={currentStep >= 3}
                  isCompleted={currentStep > 3}
                />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    Confirmation
                  </p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Cart Items & Payment Form */}
              <div className="lg:col-span-2">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">
                  Your Cart - {cartItems.length} items
                </h1>

                {cartItems.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <svg
                      className="w-16 h-16 text-gray-300 mx-auto mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      Your cart is empty
                    </h2>
                    <p className="text-gray-600">
                      Add some services to get started!
                    </p>
                    <WhiteLabelButton
                      onClick={() => navigate("/patient/dashboard")}
                      className="mt-4 text-white px-6 py-2 rounded-md"
                    >
                      Browse Services
                    </WhiteLabelButton>
                  </div>
                ) : (
                  <>
                    {/* Cart Items */}
                    <div className="space-y-4 mb-8">
                      {cartItems.map((item) => (
                        <div
                          key={item.product.product_id}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                {item.product.name}
                              </h3>
                              <p
                                className="text-2xl font-bold"
                                style={{ color: primaryColor }}
                              >
                                ${item.product.final_price.toFixed(2)}
                              </p>
                            </div>

                            <div className="flex items-center space-x-4">
                              {/* Quantity Controls */}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.product.product_id,
                                      item.quantity - 1
                                    )
                                  }
                                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                                  disabled={item.quantity <= 1}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M20 12H4"
                                    />
                                  </svg>
                                </button>
                                <span className="w-8 text-center font-semibold">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.product.product_id,
                                      item.quantity + 1
                                    )
                                  }
                                  disabled={true}
                                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                </button>
                              </div>

                              {/* Delete Button */}
                              <button
                                onClick={() =>
                                  removeItem(item.product.product_id)
                                }
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Remove item"
                              >
                                <svg
                                  className="w-5 h-5 group-hover:scale-110 transition-transform"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Item Total */}
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Item total:</span>
                              <span className="font-bold text-gray-900">
                                $
                                {(
                                  item.product.final_price * item.quantity
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Payment Form */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Credit card checkout
                      </h2>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Card Information
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="cardNumber"
                              value={cardInfo.cardNumber}
                              onChange={handleCardInputChange}
                              onBlur={handleBlur}
                              placeholder="5590 4902 0634 5421"
                              // placeholder="1234 1234 1234 1234"
                              maxLength={19}
                              className={`w-full px-3 py-2 border ${
                                errors.cardNumber && touched.cardNumber
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-md focus:outline-none`}
                              style={{ borderColor: primaryColor }}
                              required
                            />
                            <div className="absolute right-3 top-2 flex space-x-1">
                              <div
                                className="h-6 w-9 rounded flex items-center justify-center"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <span className="text-white text-xs font-bold">
                                  VISA
                                </span>
                              </div>
                              <div className="h-6 w-9 bg-red-500 rounded flex items-center justify-center">
                                <div className="flex">
                                  <div className="w-3 h-3 bg-yellow-400 rounded-full -mr-1"></div>
                                  <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {errors.cardNumber && touched.cardNumber && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.cardNumber}
                            </p>
                          )}

                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <input
                                type="text"
                                name="expiryDate"
                                value={cardInfo.expiryDate}
                                onChange={handleCardInputChange}
                                onBlur={handleBlur}
                                placeholder="MM / YY"
                                maxLength={5}
                                className={`w-full px-3 py-2 border ${
                                  errors.expiryDate && touched.expiryDate
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md focus:outline-none`}
                                style={{ borderColor: primaryColor }}
                                required
                              />
                              {errors.expiryDate && touched.expiryDate && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.expiryDate}
                                </p>
                              )}
                            </div>
                            <div>
                              <input
                                type="text"
                                name="cvv"
                                value={cardInfo.cvv}
                                onChange={handleCardInputChange}
                                onBlur={handleBlur}
                                placeholder="CVC"
                                maxLength={4}
                                className={`w-full px-3 py-2 border ${
                                  errors.cvv && touched.cvv
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md focus:outline-none`}
                                style={{ borderColor: primaryColor }}
                                required
                              />
                              {errors.cvv && touched.cvv && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.cvv}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cardholder name
                          </label>
                          <input
                            type="text"
                            name="cardholderName"
                            value={cardInfo.cardholderName}
                            onChange={handleCardInputChange}
                            onBlur={handleBlur}
                            placeholder="Full name on card"
                            className={`w-full px-3 py-2 border ${
                              errors.cardholderName && touched.cardholderName
                                ? "border-red-500"
                                : "border-gray-300"
                            } rounded-md focus:outline-none`}
                            style={{ borderColor: primaryColor }}
                            required
                          />
                          {errors.cardholderName && touched.cardholderName && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.cardholderName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Billing address
                          </label>
                          <select
                            name="country"
                            value={billingAddress.country}
                            onChange={handleBillingInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none mb-2"
                            style={{ borderColor: primaryColor }}
                            required
                          >
                            <option value="US">United States</option>
                            <option value="MA">Morocco</option>
                          </select>

                          <input
                            type="text"
                            name="address"
                            value={billingAddress.address}
                            onChange={handleBillingInputChange}
                            onBlur={handleBlur}
                            placeholder="Address line 1"
                            className={`w-full px-3 py-2 border ${
                              errors.address && touched.address
                                ? "border-red-500"
                                : "border-gray-300"
                            } rounded-md focus:outline-none mb-2`}
                            style={{ borderColor: primaryColor }}
                            required
                          />
                          {errors.address && touched.address && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.address}
                            </p>
                          )}

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <input
                                type="text"
                                name="city"
                                value={billingAddress.city}
                                onChange={handleBillingInputChange}
                                onBlur={handleBlur}
                                placeholder="City"
                                className={`w-full px-3 py-2 border ${
                                  errors.city && touched.city
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md focus:outline-none`}
                                style={{ borderColor: primaryColor }}
                                required
                              />
                              {errors.city && touched.city && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.city}
                                </p>
                              )}
                            </div>
                            <div>
                              <input
                                type="text"
                                name="state"
                                value={billingAddress.state}
                                onChange={handleBillingInputChange}
                                onBlur={handleBlur}
                                placeholder="State"
                                className={`w-full px-3 py-2 border ${
                                  errors.state && touched.state
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md focus:outline-none`}
                                style={{ borderColor: primaryColor }}
                                required
                              />
                              {errors.state && touched.state && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.state}
                                </p>
                              )}
                            </div>
                          </div>

                          <input
                            type="text"
                            name="postalCode"
                            value={billingAddress.postalCode}
                            onChange={handleBillingInputChange}
                            onBlur={handleBlur}
                            placeholder="Postal code"
                            className={`w-full mt-2 px-3 py-2 border ${
                              errors.postalCode && touched.postalCode
                                ? "border-red-500"
                                : "border-gray-300"
                            } rounded-md focus:outline-none`}
                            style={{ borderColor: primaryColor }}
                            required
                          />
                          {errors.postalCode && touched.postalCode && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.postalCode}
                            </p>
                          )}
                        </div>

                        {errors.submit && (
                          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                            {errors.submit}
                          </div>
                        )}

                        <div className="text-xs text-black-450 bg-white-50 p-3 rounded-md flex justify-center items-center gap-2 w-full">
                          <span>Powered by</span>
                          <div className="relative w-[25px] h-[26px]">
                            <a
                              href="https://gatewayguardian.com/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              {/* Outer Blue Circle SVG */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="25"
                                height="26"
                                viewBox="0 0 25 26"
                                fill="none"
                                className="w-full h-full"
                              >
                                <path
                                  d="M0.0800781 10.9417C0.0800781 6.78479 0.0800783 4.70636 0.583438 4.00713C1.08678 3.3079 3.09117 2.63895 7.09996 1.30103L7.8637 1.04614C9.95341 0.348712 10.9982 0 12.0801 0C13.1619 0 14.2067 0.348712 16.2965 1.04614L17.0602 1.30103C21.069 2.63895 23.0734 3.3079 23.5767 4.00713C24.0801 4.70636 24.0801 6.78479 24.0801 10.9417C24.0801 11.5695 24.0801 12.2504 24.0801 12.9888C24.0801 20.3182 18.4281 23.8751 14.8819 25.3855C13.9201 25.7951 13.4391 26 12.0801 26C10.721 26 10.2401 25.7951 9.27816 25.3855C5.73202 23.8751 0.0800781 20.3182 0.0800781 12.9888C0.0800781 12.2504 0.0800781 11.5695 0.0800781 10.9417Z"
                                  fill="#166FF5"
                                  fill-opacity="0.91"
                                />
                              </svg>

                              {/* Inner White Shape SVG */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                className="absolute inset-0 m-auto w-[14px] h-[14px]"
                              >
                                <path
                                  d="M3.02804 2.24134C5.70511 0.028272 8.70688 -0.545903 12.0319 0.518688C13.2883 0.980103 14.3946 1.67353 15.3513 2.59779C15.3763 2.62277 15.3763 2.648 15.3513 2.67298C14.1745 3.82893 13.03 4.9895 11.9187 6.15541C13.7844 6.18046 15.654 6.20573 17.5271 6.2306C17.5559 6.2306 17.5376 6.2306 17.5564 6.2306C18.5283 9.53429 17.8991 12.4307 15.6892 15.0695C13.4595 17.3888 10.7397 18.3317 7.52999 17.8976C3.91395 17.1587 1.51959 15.0422 0.346397 11.549C-0.567214 7.91916 0.326296 4.81628 3.02804 2.24134ZM11.8444 6.15541C10.9392 5.2777 9.85121 4.88447 8.58077 4.97572C6.84105 5.2524 5.68454 6.21998 5.11105 7.87806C4.79899 9.13085 4.99854 10.2923 5.71065 11.3615C6.67055 12.6093 7.93988 13.1521 9.51827 12.9904C11.0253 12.7631 12.0881 11.9583 12.7067 10.5753C10.975 10.5504 9.23701 10.5251 7.49288 10.5001C7.49752 10.4776 7.50986 10.4654 7.52999 10.463C7.55501 10.4506 7.58016 10.4374 7.60519 10.4249C9.0174 9.00254 10.4307 7.57938 11.8444 6.15541Z"
                                  fill="white"
                                />
                              </svg>
                            </a>
                          </div>
                          <a
                            href="https://gatewayguardian.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-black-600 hover:text-black-800 transition-colors"
                          >
                            <span className="ml-1 font-semibold text-black-600">
                              Gateway Guardian
                            </span>
                          </a>
                        </div>

                        <WhiteLabelButton
                          onClick={handleProceed}
                          disabled={isProcessing}
                          className="w-full text-white py-3 px-4 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {isProcessing ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            "Proceed to Payment"
                          )}
                        </WhiteLabelButton>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Right Column - Order Summary */}
              <div>
                <div
                  className="text-white rounded-lg shadow-sm p-6 sticky top-4"
                  style={{ backgroundColor: primaryColor }}
                >
                  <h2 className="text-lg font-semibold mb-4 flex items-center">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    Order summary {cartItems.length} items
                  </h2>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      {cartItems.map((item) => (
                        <div
                          key={item.product.product_id}
                          className="flex justify-between text-sm"
                        >
                          <span className="opacity-90">
                            {item.product.name} x{item.quantity}
                          </span>
                          <span>
                            $
                            {(item.product.final_price * item.quantity).toFixed(
                              2
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-white-500 pt-3">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="opacity-90"># of Items</span>
                        <span>
                          {cartItems.reduce(
                            (total, item) => total + item.quantity,
                            0
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-lg font-semibold">Total</span>
                        <span className="text-lg font-semibold">
                          ${subtotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 text-sm opacity-90">
                    All prices include applicable taxes. Final pricing will be
                    confirmed during checkout.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PatientFooter />
      </div>
    );
  }

  // Step 2: Payment Processing with iFrame
  if (currentStep === 2 && paymentUrl) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Header */}
        <PatientHeader />

        {/* Progress Steps */}
        <div className="bg-white py-8">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <StepIcon step={1} isActive={true} isCompleted={true} />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Your cart</p>
                  <p className="text-xs text-gray-500">Complete</p>
                </div>
              </div>

              <div className="flex-1 mx-4">
                <div className="h-0.5 bg-gray-300 relative">
                  <div
                    className="absolute left-0 top-0 h-full w-full"
                    style={{ backgroundColor: primaryColor }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center">
                <StepIcon step={2} isActive={true} isCompleted={false} />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Payment</p>
                  <p className="text-xs text-gray-500">Processing</p>
                </div>
              </div>

              <div className="flex-1 mx-4">
                <div className="h-0.5 bg-gray-300 relative">
                  <div
                    className="absolute left-0 top-0 h-full w-0"
                    style={{ backgroundColor: primaryColor }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center">
                <StepIcon step={3} isActive={false} isCompleted={false} />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">
                    Confirmation
                  </p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Complete Your Payment
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                You will be redirected to our secure payment processor to
                complete your transaction.
              </p>

              {/* Payment iFrame */}
              <div
                className="relative bg-gray-50 rounded-lg"
                style={{ height: "600px" }}
              >
                <iframe
                  src={paymentUrl}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  className="rounded-lg"
                  title="Payment Processing"
                />
              </div>

              <div className="mt-6 text-center">
                <WhiteLabelButton
                  onClick={() => setCurrentStep(3)}
                  // className="text-blue-600 hover:underline text-sm"
                  className="w-full text-white py-3 px-4 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  Proceed
                </WhiteLabelButton>
              </div>
            </div>
          </div>
        </div>

        <PatientFooter />
      </div>
    );
  }

  // Step 3: Thank You with Receipt
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <PatientHeader />

      {/* Thank You Content */}
      <div className="flex-1">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Thank you for your order!
            </h1>

            <p className="text-lg text-gray-600 mb-2">
              Your order has been successfully processed.
            </p>

            {orderId && (
              <p className="text-sm text-gray-500">
                Order ID: <span className="font-mono">{orderId}</span>
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              What happens next?
            </h2>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                You'll receive an order confirmation email shortly
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Your clinic will be notified about your order
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                You'll receive further instructions for sample collection
              </li>
            </ul>
          </div>

          <div className="flex justify-center gap-3">
            <button
              className="bg-white border border-gray-300 text-gray-700 px-2 sm:px-6 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium flex items-center text-[13px] sm:text-base"
              onClick={handleOrderReceipt}
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                />
              </svg>
              Download receipt
            </button>

            <WhiteLabelButton
              onClick={() => navigate("/patient/dashboard")}
              className="text-white px-2 sm:px-6 py-3 text-[13px] sm:text-base rounded-md font-medium"
            >
              Back to Dashboard
            </WhiteLabelButton>
          </div>

          {/* Help Section */}
          <div className="mt-16 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-6 w-6"
                style={{ color: primaryColor }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Need help?
            </h3>
            <p className="text-gray-600 mb-4">
              Our support team is here to assist you
            </p>

            <button
              className="hover:text-gray-300 font-medium"
              style={{ color: primaryColor }}
              onClick={() =>
                window.open(contactLink, "_blank", "noopener,noreferrer")
              }
              aria-label="Contact Support"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>

      <PatientFooter />
    </div>
  );
};

export default PatientCheckout;
